import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar";
import Header from "../Header";
import "./tpl.css";
import "./Users.css";
import axios from "axios";
import API_BASE_URL from "../../config";
import {
  BsPeopleFill,
  BsPencil,
  BsTrash3Fill,
  BsXLg,
  BsCheckCircleFill,
  BsXCircleFill,
  BsExclamationTriangleFill,
  BsShieldLockFill,
  BsPersonFill,
} from "react-icons/bs";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { fetchUsers(); }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("userToken");
      if (!token) { window.location.href = "/login"; return; }
      const headers = { Authorization: `Bearer ${token}` };
      const userRes = await axios.get(`${API_BASE_URL}/users`, { headers });
      setUsers(userRes.data);
      const adminRes = await axios.get(`${API_BASE_URL}/profile`, { headers });
      setCompanyId(adminRes.data.company_id);
      setIsAdmin(adminRes.data.role === "admin");
    } catch (err) {
      console.error("Error fetching users:", err.response?.data || err.message);
      setError("Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => setSelectedUser({ ...user });

  const handleAddUser = () => {
    setSelectedUser({ user_id: null, first_name: "", last_name: "", email: "", phone: "", role: "user", company_id: companyId });
  };

  const handleSaveOrUpdate = async () => {
    if (!selectedUser.first_name || !selectedUser.last_name || !selectedUser.email || !selectedUser.phone || !selectedUser.role) {
      showToast("error", "All fields are required.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("userToken");
      const headers = { Authorization: `Bearer ${token}` };
      const data = {
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        email: selectedUser.email,
        phone: selectedUser.phone,
        role: selectedUser.role.toLowerCase(),
        company_id: selectedUser.company_id || companyId,
      };
      if (selectedUser.user_id) {
        await axios.put(`${API_BASE_URL}/users/${selectedUser.user_id}`, data, { headers });
        showToast("success", "User updated successfully.");
      } else {
        await axios.post(`${API_BASE_URL}/users`, data, { headers });
        showToast("success", "User created successfully.");
      }
      fetchUsers();
      setSelectedUser(null);
    } catch (err) {
      console.error("Error saving user:", err.response?.data || err.message);
      showToast("error", err.response?.data?.error || "Failed to save user.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;
    try {
      const token = localStorage.getItem("userToken");
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_BASE_URL}/users/${deleteUserId}`, { headers });
      showToast("success", "User deleted.");
      fetchUsers();
      setDeleteUserId(null);
    } catch (err) {
      console.error("Error deleting user:", err.response?.data || err.message);
      showToast("error", "Failed to delete user.");
    }
  };

  const roleTag = (role) => role === "admin"
    ? <span className="tpl-tag info"><BsShieldLockFill /> Admin</span>
    : <span className="tpl-tag off"><BsPersonFill /> User</span>;

  return (
    <div className="page-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4">
          <div className="page-header dash-head">
            <div>
              <h4>Users</h4>
              <p className="page-subtitle">Manage who can access your Pairfon account.</p>
            </div>
            {isAdmin && (
              <button className="tpl-cta" onClick={handleAddUser}>＋ Invite user</button>
            )}
          </div>

          {loading ? (
            <div className="page-loading"><div className="spinner" /><p>Loading users…</p></div>
          ) : error ? (
            <div className="page-error"><BsXCircleFill size={32} /><p>{error}</p></div>
          ) : users.length === 0 ? (
            <div className="tpl-card"><div className="tpl-row" style={{ border: 0 }}><BsPeopleFill size={28} className="tpl-mut" /><div><div className="nm">No users yet</div><div className="sub">Invite your first team member.</div></div></div></div>
          ) : (
            <div className="tpl-card">
              <div className="tpl-card-h"><h6>Team</h6></div>
              <div className="table-responsive">
                <table className="tpl-table">
                  <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th></th></tr></thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.user_id || index}>
                        <td>
                          <div className="usr-name">
                            <div className="usr-avatar">{(user.first_name || "?")[0]}{(user.last_name || "?")[0]}</div>
                            <span>{user.first_name} {user.last_name}</span>
                          </div>
                        </td>
                        <td className="tpl-mut">{user.email}</td>
                        <td className="tpl-mut">{user.phone}</td>
                        <td>{roleTag(user.role)}</td>
                        <td className="tpl-actions">
                          <button className="tpl-ic" onClick={() => handleEditClick(user)} title="Edit"><BsPencil /></button>
                          <button className="tpl-ic danger" onClick={() => setDeleteUserId(user.user_id)} title="Delete"><BsTrash3Fill /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="tpl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tpl-modal-head">
              <h5>{selectedUser.user_id ? "Edit user" : "Invite user"}</h5>
              <button className="tpl-modal-close" onClick={() => setSelectedUser(null)}><BsXLg /></button>
            </div>
            <div className="tpl-fields">
              <div className="tpl-fgrid">
                <div className="tpl-field">
                  <label>First name</label>
                  <input value={selectedUser.first_name} onChange={(e) => setSelectedUser({ ...selectedUser, first_name: e.target.value })} placeholder="First name" />
                </div>
                <div className="tpl-field">
                  <label>Last name</label>
                  <input value={selectedUser.last_name} onChange={(e) => setSelectedUser({ ...selectedUser, last_name: e.target.value })} placeholder="Last name" />
                </div>
              </div>
              <div className="tpl-field">
                <label>Email</label>
                <input type="email" value={selectedUser.email} onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })} placeholder="Email address" />
              </div>
              <div className="tpl-field">
                <label>Phone</label>
                <input value={selectedUser.phone} onChange={(e) => setSelectedUser({ ...selectedUser, phone: e.target.value })} placeholder="Phone number" />
              </div>
              <div className="tpl-field">
                <label>Role</label>
                <select value={selectedUser.role} onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="tpl-modal-foot">
              <button className="tpl-btn ghost" onClick={() => setSelectedUser(null)}>Cancel</button>
              <button className="tpl-btn" onClick={handleSaveOrUpdate} disabled={saving}>
                {saving ? "Saving…" : selectedUser.user_id ? "Update" : "Create user"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteUserId && (
        <div className="modal-overlay" onClick={() => setDeleteUserId(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon-wrap modal-icon-danger"><BsExclamationTriangleFill /></div>
            <h5>Delete user?</h5>
            <p>This will permanently remove the user from your team. This cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setDeleteUserId(null)}>Cancel</button>
              <button className="btn-modal-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast-notification ${toast.type === "success" ? "toast-success" : "toast-error"}`}>
          {toast.type === "success" ? <BsCheckCircleFill /> : <BsXCircleFill />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
